'use client';

import { Box, Button, Chip, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import { AppHealthResponse, ServiceHealth } from '@repo/utils';
import { useEffect, useMemo, useState } from 'react';
import { Logo } from '../../assets';
import { COLORS } from '../../constants';

const STATUS_DETAILS = {
  UP: {
    label: 'Saludable',
    chipColor: 'success' as const,
    symbol: '✓',
    accent: '#E8F5E9',
    border: '#66BB6A',
    description: 'Todas las dependencias monitoreadas están operando normalmente.',
  },
  DOWN: {
    label: 'Degradado',
    chipColor: 'error' as const,
    symbol: '✕',
    accent: '#FFEBEE',
    border: '#E53935',
    description: 'Uno o más servicios no están disponibles.',
  },
  TIMEOUT: {
    label: 'Lento',
    chipColor: 'warning' as const,
    symbol: '⏱',
    accent: '#FFF4E5',
    border: '#FB8C00',
    description: 'Algunas respuestas son más lentas de lo esperado.',
  },
  ERROR: {
    label: 'Problema',
    chipColor: 'default' as const,
    symbol: '⚠',
    accent: '#F4F4F4',
    border: '#9E9E9E',
    description: 'Una dependencia devolvió una condición de error.',
  },
};

type StatusKey = keyof typeof STATUS_DETAILS;

const getStatusDetails = (status: string) => STATUS_DETAILS[(status as StatusKey) ?? 'ERROR'] ?? STATUS_DETAILS.ERROR;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the commented-out services panel
const formatResponseTime = (responseTime?: number) => (responseTime != null ? `${responseTime} ms` : 'Sin datos');

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the commented-out services panel
const getServiceSummary = (service: ServiceHealth) =>
  service.message ?? (service.status === 'UP' ? 'Operativo' : 'Necesita atención');

/**
 * Health status page shared by every app; polls the app's /api/health route.
 *
 * @param {string} appName - Display name of the app in the description.
 * @returns {React.JSX.Element} The rendered status page.
 */
export const StatusPage = ({ appName }: { appName: string }) => {
  const [health, setHealth] = useState<AppHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/health', { cache: 'no-store' });
      const data = await response.json();
      setHealth(data);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Unable to fetch health status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, 30000);
    return () => clearInterval(timer);
  }, []);

  const summary = useMemo(() => {
    if (!health) {
      return { total: 0, up: 0, down: 0, timeout: 0 };
    }

    const items = Object.values(health.checks);
    return {
      total: items.length,
      up: items.filter((item) => item.status === 'UP').length,
      down: items.filter((item) => item.status === 'DOWN').length,
      timeout: items.filter((item) => item.status === 'TIMEOUT').length,
    };
  }, [health]);

  // Only the first load replaces the page; the 30s background refetches keep the current content on screen
  if (loading && !health) {
    return (
      <Box sx={{ bgcolor: '#f4f7fb', minHeight: '100vh', py: 12 }}>
        <Container maxWidth="sm">
          <Paper role="status" aria-busy="true" sx={{ p: 6, textAlign: 'center', boxShadow: 4, borderRadius: 4 }}>
            <CircularProgress size={52} aria-hidden="true" />
            <Typography component="h1" variant="h6" sx={{ mt: 3, color: COLORS.text.main }}>
              Verificando estado de salud...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error || !health) {
    return (
      <Box sx={{ bgcolor: '#f4f7fb', minHeight: '100vh', py: 12 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, textAlign: 'center', boxShadow: 4, borderRadius: 4 }}>
            <Typography component="h1" variant="h6" sx={{ color: COLORS.state.error, mb: 2 }}>
              No se pudo cargar el estado
            </Typography>
            <Typography sx={{ color: COLORS.text.light, mb: 4 }}>{error || 'Ocurrió un error inesperado.'}</Typography>
            <Button variant="contained" onClick={fetchHealth}>
              Intentar de nuevo
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const overall = getStatusDetails(health.status);

  return (
    <Box aria-busy={loading} sx={{ bgcolor: '#f4f7fb', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Logo height={60} />
        </Box>
        <Paper
          sx={{
            p: 4,
            mb: 3,
            boxShadow: 4,
            borderRadius: 4,
            border: `1px solid ${COLORS.text.light4}`,
            background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
          }}
        >
          <Stack spacing={4}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: COLORS.text.light }}>
                  Panel de salud
                </Typography>
                <Typography component="h1" variant="h3" sx={{ mt: 1, fontWeight: 800, color: COLORS.text.main }}>
                  Estado del sistema
                </Typography>
                <Typography sx={{ mt: 1.5, color: COLORS.text.light, maxWidth: 640 }}>
                  Una vista ligera del estado para las dependencias del servicio {appName}.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Chip
                  label={health.status}
                  color={overall.chipColor}
                  sx={{ minWidth: 116, fontWeight: 700, py: 1.2 }}
                />
                <Button variant="outlined" onClick={fetchHealth}>
                  Actualizar
                </Button>
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Paper
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 4,
                  bgcolor: '#fff',
                  boxShadow: 1,
                  border: `1px solid ${COLORS.text.light4}`,
                }}
              >
                <Typography component="p" variant="subtitle2" sx={{ color: COLORS.text.light, mb: 1 }}>
                  Salud general
                </Typography>
                <Typography component="p" variant="h5" sx={{ fontWeight: 700, color: COLORS.text.main }}>
                  {overall.label}
                </Typography>
                <Typography sx={{ mt: 1, color: COLORS.text.light }}>{overall.description}</Typography>
              </Paper>

              <Paper
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 4,
                  bgcolor: '#fff',
                  boxShadow: 1,
                  border: `1px solid ${COLORS.text.light4}`,
                }}
              >
                <Typography component="p" variant="subtitle2" sx={{ color: COLORS.text.light, mb: 1 }}>
                  Conteo de servicios
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip label={`Total ${summary.total}`} sx={{ fontWeight: 700 }} />
                  <Chip label={`Saludables ${summary.up}`} color="success" sx={{ fontWeight: 700 }} />
                  <Chip label={`Degradados ${summary.down}`} color="error" sx={{ fontWeight: 700 }} />
                  <Chip label={`Lentos ${summary.timeout}`} color="warning" sx={{ fontWeight: 700 }} />
                </Stack>
              </Paper>
            </Stack>
          </Stack>
        </Paper>

        {/* <Paper sx={{ p: 3, mb: 3, boxShadow: 4, borderRadius: 5, border: `1px solid ${COLORS.text.light4}`, bgcolor: '#fff' }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
              <Box>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: COLORS.text.main }}>
                  Servicios monitoreados
                </Typography>
                <Typography sx={{ color: COLORS.text.light }}>Estado del servicio con detalles mínimos de respuesta.</Typography>
              </Box>
              <Button variant="text" onClick={fetchHealth}>
                Recargar ahora
              </Button>
            </Stack>

            <Stack spacing={2}>
              {Object.entries(health.checks).map(([serviceName, service]: [string, ServiceHealth]) => {
                const serviceStatus = getStatusDetails(service.status);
                return (
                  <Paper
                    key={serviceName}
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      borderLeft: `4px solid ${serviceStatus.border}`,
                      bgcolor: '#fafbff',
                      boxShadow: 1,
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                        <Typography sx={{ color: COLORS.text.light2 }}>Tiempo de respuesta</Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color:
                              service.responseTime == null
                                ? COLORS.text.light
                                : service.responseTime < 500
                                ? COLORS.primary.green.dark
                                : COLORS.state.warning,
                          }}
                        >
                          {formatResponseTime(service.responseTime)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Paper> */}

        <Paper
          sx={{
            p: 2.5,
            textAlign: 'center',
            bgcolor: '#fff',
            borderRadius: 4,
            border: `1px solid ${COLORS.text.light4}`,
            boxShadow: 1,
          }}
        >
          <Typography sx={{ color: COLORS.text.light2, fontSize: '0.93rem' }}>
            Última actualización: {new Date(health.timestamp).toLocaleString()} · Actualización automática cada 30
            segundos
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};
